import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AdminPageShellComponent } from '../../../shared/components/admin/admin-page-shell/admin-page-shell.component';
import { AdminToggleSwitchComponent } from '../../../shared/components/admin/admin-toggle-switch/admin-toggle-switch.component';
import { CtaButtonComponent } from '../../../shared/components/ui/cta-button/cta-button.component';
import { AppToasterService } from '../../../core/services/toaster.service';
import {
  AUDIENCE_FROM_DOMAINS,
  AUDIENCE_TAGS,
  AudienceCampaign,
  AudienceFromDomain,
  AudienceTag,
  PostmarkTemplateOption,
  normalizeAudienceTag,
} from './models/audience.model';
import { AudienceAdminService } from './services/audience-admin.service';

@Component({
  selector: 'app-audience-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    MatProgressSpinnerModule,
    AdminPageShellComponent,
    AdminToggleSwitchComponent,
    CtaButtonComponent,
  ],
  templateUrl: './audience.component.html',
  styleUrl: './audience.component.scss',
})
export class AudienceAdminComponent implements OnInit {
  #api = inject(AudienceAdminService);
  #toastr = inject(AppToasterService);
  #translate = inject(TranslateService);

  readonly tags = AUDIENCE_TAGS;
  readonly fromDomains = AUDIENCE_FROM_DOMAINS;
  readonly filterTag = signal<string>('TEST');
  readonly contactsTotal = signal(0);
  readonly campaigns = signal<AudienceCampaign[]>([]);
  readonly templates = signal<PostmarkTemplateOption[]>([]);
  readonly loadingContacts = signal(false);
  readonly loadingCampaigns = signal(false);
  readonly loadingTemplates = signal(false);
  readonly busy = signal(false);
  readonly hideCompletedCampaigns = signal(false);
  readonly visibleCampaigns = computed(() => {
    const rows = this.campaigns();
    if (!this.hideCompletedCampaigns()) {
      return rows;
    }
    return rows.filter(
      (campaign) =>
        campaign.status !== 'completed' && campaign.status !== 'archived',
    );
  });

  // Upsert form
  upsertEmail = '';
  upsertName = '';
  upsertTags: string[] = ['TEST'];
  upsertCustomTag = '';

  // Create campaign form
  campaignName = '';
  campaignTags: string[] = ['TEST'];
  campaignCustomTag = '';
  campaignTagMatch: 'any' | 'all' = 'any';
  campaignFromDomain: AudienceFromDomain = 'valvur.gg';
  campaignTemplateId: number | null = null;
  campaignBatchSize = 100;
  campaignMaxBatches = 1;
  campaignDryRun = true;

  readonly campaignTagMatchOptions: Array<{
    value: 'any' | 'all';
    labelKey: string;
  }> = [
    { value: 'any', labelKey: 'app.admin.audience.tagMatchAny' },
    { value: 'all', labelKey: 'app.admin.audience.tagMatchAll' },
  ];

  ngOnInit(): void {
    void this.reload();
  }

  async reload(): Promise<void> {
    await Promise.all([
      this.loadContacts(),
      this.loadCampaigns(),
      this.loadTemplates(),
    ]);
  }

  async loadTemplates(): Promise<void> {
    this.loadingTemplates.set(true);
    try {
      const rows = await this.#api.listTemplates();
      this.templates.set(rows);
      if (
        this.campaignTemplateId == null ||
        !rows.some((t) => t.templateId === this.campaignTemplateId)
      ) {
        const preferred =
          rows.find((t) => t.alias === 'code-your-own-14') ?? rows[0];
        this.campaignTemplateId = preferred?.templateId ?? null;
      }
    } catch {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.loadTemplatesError'),
      );
    } finally {
      this.loadingTemplates.set(false);
    }
  }

  async loadContacts(): Promise<void> {
    this.loadingContacts.set(true);
    try {
      const res = await this.#api.listContacts({
        tags: [this.filterTag()],
        match: 'any',
        limit: 1,
      });
      this.contactsTotal.set(res.total);
    } catch {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.loadContactsError'),
      );
    } finally {
      this.loadingContacts.set(false);
    }
  }

  async loadCampaigns(): Promise<void> {
    this.loadingCampaigns.set(true);
    try {
      const rows = await this.#api.listCampaigns();
      this.campaigns.set(rows);
    } catch {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.loadCampaignsError'),
      );
    } finally {
      this.loadingCampaigns.set(false);
    }
  }

  async onFilterTagChange(tag: AudienceTag): Promise<void> {
    this.filterTag.set(tag);
    await this.loadContacts();
  }

  async upsertContact(): Promise<void> {
    const email = this.upsertEmail.trim();
    if (!email) return;
    if (!this.upsertTags.length) {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.contactTagsRequired'),
      );
      return;
    }
    const name = this.upsertName.trim() || undefined;
    this.busy.set(true);
    try {
      await this.#api.upsertContact(email, [...this.upsertTags], name);
      this.upsertEmail = '';
      this.upsertName = '';
      this.#toastr.success(
        this.#translate.instant('app.admin.audience.contactSaved'),
      );
      await this.loadContacts();
    } catch {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.contactSaveError'),
      );
    } finally {
      this.busy.set(false);
    }
  }

  async createCampaign(): Promise<void> {
    if (!this.campaignTags.length) {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.campaignTagsRequired'),
      );
      return;
    }
    if (this.campaignTemplateId == null) {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.templateRequired'),
      );
      return;
    }
    this.busy.set(true);
    try {
      await this.#api.createCampaign({
        name: this.campaignName.trim() || undefined,
        tags: [...this.campaignTags],
        match: this.campaignTagMatch,
        templateId: Number(this.campaignTemplateId),
        fromDomain: this.campaignFromDomain,
        // Contact name is mapped to {{name}} at send time.
        variables: { name: '' },
        batchSize: Number(this.campaignBatchSize),
        maxBatches: Number(this.campaignMaxBatches),
        dryRun: this.campaignDryRun,
      });
      this.#toastr.success(
        this.#translate.instant('app.admin.audience.campaignCreated'),
      );
      await this.loadCampaigns();
    } catch {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.campaignCreateError'),
      );
    } finally {
      this.busy.set(false);
    }
  }

  async updateCampaignSettings(campaign: AudienceCampaign): Promise<void> {
    if (!this.canEditSettings(campaign)) {
      return;
    }

    this.busy.set(true);
    try {
      const updated = await this.#api.updateCampaignSettings(campaign.id, {
        batchSize: Number(campaign.batchSize),
        maxBatches: Number(campaign.maxBatches),
      });
      this.replaceCampaign(updated);
      this.#toastr.success(
        this.#translate.instant('app.admin.audience.campaignSettingsSaved'),
      );
    } catch {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.campaignSettingsSaveError'),
      );
    } finally {
      this.busy.set(false);
    }
  }

  async startCampaign(campaign: AudienceCampaign): Promise<void> {
    if (!this.canStart(campaign)) {
      return;
    }
    this.busy.set(true);
    try {
      await this.#api.startCampaign(campaign.id, {
        batchSize: campaign.batchSize,
        maxBatches: campaign.maxBatches,
        dryRun: campaign.dryRun,
      });
      this.#toastr.success(
        this.#translate.instant('app.admin.audience.campaignStarted'),
      );
      await this.loadCampaigns();
    } catch {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.campaignStartError'),
      );
    } finally {
      this.busy.set(false);
    }
  }

  async archiveCampaign(campaign: AudienceCampaign): Promise<void> {
    if (!this.canArchive(campaign)) {
      return;
    }

    this.busy.set(true);
    try {
      const updated = await this.#api.archiveCampaign(campaign.id);
      this.replaceCampaign(updated);
      this.#toastr.success(
        this.#translate.instant('app.admin.audience.campaignArchived'),
      );
    } catch {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.campaignArchiveError'),
      );
    } finally {
      this.busy.set(false);
    }
  }

  canEditSettings(campaign: AudienceCampaign): boolean {
    return (
      campaign.status !== 'running' &&
      campaign.status !== 'completed' &&
      campaign.status !== 'cancelled' &&
      campaign.status !== 'archived'
    );
  }

  canStart(campaign: AudienceCampaign): boolean {
    return (
      campaign.remainingCount > 0 &&
      campaign.status !== 'running' &&
      campaign.status !== 'completed' &&
      campaign.status !== 'cancelled' &&
      campaign.status !== 'archived'
    );
  }

  canArchive(campaign: AudienceCampaign): boolean {
    return (
      campaign.status !== 'running' && campaign.status !== 'archived'
    );
  }

  onHideCompletedChange(hide: boolean): void {
    this.hideCompletedCampaigns.set(hide);
  }

  isUpsertTagSelected(tag: string): boolean {
    return this.upsertTags.includes(tag);
  }

  isCampaignTagSelected(tag: string): boolean {
    return this.campaignTags.includes(tag);
  }

  toggleUpsertTag(tag: string): void {
    this.upsertTags = this.toggleInList(this.upsertTags, tag);
  }

  toggleCampaignTag(tag: string): void {
    this.campaignTags = this.toggleInList(this.campaignTags, tag);
  }

  upsertCustomTags(): string[] {
    return this.upsertTags.filter(
      (tag) => !AUDIENCE_TAGS.includes(tag as AudienceTag),
    );
  }

  campaignCustomTags(): string[] {
    return this.campaignTags.filter(
      (tag) => !AUDIENCE_TAGS.includes(tag as AudienceTag),
    );
  }

  addUpsertCustomTag(): void {
    const next = this.addNormalizedTag(this.upsertTags, this.upsertCustomTag);
    if (!next) return;
    this.upsertTags = next;
    this.upsertCustomTag = '';
  }

  addCampaignCustomTag(): void {
    const next = this.addNormalizedTag(
      this.campaignTags,
      this.campaignCustomTag,
    );
    if (!next) return;
    this.campaignTags = next;
    this.campaignCustomTag = '';
  }

  onUpsertCustomTagKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addUpsertCustomTag();
    }
  }

  onCampaignCustomTagKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addCampaignCustomTag();
    }
  }

  tagMatchLabel(match: 'any' | 'all'): string {
    return match === 'all'
      ? this.#translate.instant('app.admin.audience.tagMatchAllShort')
      : this.#translate.instant('app.admin.audience.tagMatchAnyShort');
  }

  campaignFromAddress(campaign: AudienceCampaign): string {
    return campaign.fromAddress || this.fromAddressFor(campaign.fromDomain);
  }

  fromAddressFor(
    domain: AudienceFromDomain | string | null | undefined,
  ): string {
    return `tere@${domain || 'valvur.gg'}`;
  }

  replaceCampaign(updated: AudienceCampaign): void {
    this.campaigns.update((rows) =>
      rows.map((row) => (row.id === updated.id ? updated : row)),
    );
  }

  templateLabel(template: PostmarkTemplateOption): string {
    const alias = template.alias ? ` (${template.alias})` : '';
    return `${template.name}${alias} — ${template.templateId}`;
  }

  private toggleInList(list: string[], tag: string): string[] {
    if (list.includes(tag)) {
      return list.filter((item) => item !== tag);
    }
    return [...list, tag];
  }

  private addNormalizedTag(
    list: string[],
    raw: string,
  ): string[] | null {
    const tag = normalizeAudienceTag(raw);
    if (!tag) {
      this.#toastr.error(
        this.#translate.instant('app.admin.audience.customTagInvalid'),
      );
      return null;
    }
    if (list.includes(tag)) {
      return list;
    }
    return [...list, tag];
  }
}
